import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";

import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  CircularProgress,
  Avatar,
  Modal,
  Divider
} from "@mui/material";
import ExpertSearch from "./ExpertSearch";

import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import Header1 from "../Header/Header1";
import Footer2 from "../Footer/Footer2";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 450,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  borderRadius: 2,
};

const RequestDetailsPage = () => {
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [experts, setExperts] = useState([]);
  const [approvedExperts, setApprovedExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingExperts, setLoadingExperts] = useState(false);
  const [excludeIds, setExcludeIds] = useState([]);
  const navigate = useNavigate();

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState(null);

  const handleOpenModal = (expert) => {
    setSelectedExpert(expert);
    setOpenModal(true);
  };
  const handleCloseModal = () => setOpenModal(false);


  // ------------------ Load Request ------------------
  useEffect(() => {
    axios.get(`${API_URL}/api/requests2/${id}/`)
      .then(res => {
        setData(res.data);
        setLoading(false);
      })
      .catch(err => setLoading(false));
  }, [id]);


  // ------------------ Load Approved Experts ------------------
  const loadApprovedExperts = () => {
    axios.get(`${API_URL}/api/get_approved_experts/${id}/`)
      .then(res => {
        setApprovedExperts(res.data);

        const ids = res.data.map(e => e.expert.id);
        setExcludeIds(ids);
      })
      .catch(err => console.error(err));
  };

  useEffect(() => {
    if (id) loadApprovedExperts();
  }, [id]);


  // ------------------ Predict Experts ------------------
  const predictExperts = () => {
    if (!data?.description) return;

    setLoadingExperts(true);

    axios.post(`${API_URL}/api/predict_experts/`, {
      description: data.description,
      request_id: id,
      exclude_ids: excludeIds
    })
      .then(res => {
        setExperts(res.data);
        setExcludeIds(prev => [...prev, ...res.data.map(e => e.expert_id)]);
        setLoadingExperts(false);
      })
      .catch(err => setLoadingExperts(false));
  };


  // ------------------ Approve Expert ------------------
  const approveExpert = (expertId) => {
    axios.post(`${API_URL}/approve_expert1/`, {
      request_id: id,
      expert_id: expertId
    })
      .then(() => loadApprovedExperts())
      .catch(err => console.error(err));
  };


  // ------------------ Revoke Expert ------------------
  const revokeExpert = (expertId) => {
    axios.post(`${API_URL}/api/revoke_expert1/`, {
      request_id: id,
      expert_id: expertId
    })
      .then(res => {
        setApprovedExperts(res.data.experts);
      })
      .catch(err => console.error(err));
  };


  // ------------------ Media Viewer ------------------
  const renderMedia = (media) => {
    if (!media) return null;

    const ext = media.split(".").pop().toLowerCase();

    if (["png","jpg","jpeg","gif"].includes(ext))
      return <CardMedia component="img" src={media} height="350" />;

    if (["mp4","webm"].includes(ext))
      return <video src={media} controls width="100%" height="350" />;

    if (["mp3","wav"].includes(ext))
      return (
        <Box textAlign="center" p={3}>
          <AudiotrackIcon fontSize="large" />
          <audio src={media} controls style={{ width: "95%" }} />
        </Box>
      );

    if (ext === "pdf")
      return <embed src={media} width="100%" height="350" type="application/pdf" />;

    return <iframe src={media} width="100%" height="350"></iframe>;
  };


  // ------------------ Loading UI ------------------
  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );

  if (!data) return <Typography align="center">Not found</Typography>;

  const handleEdit = () => {
    navigate(`/edit-request/${id}`);
  };


  // ===========================================================
  // ================= BEGIN PAGE RENDER ========================
  // ===========================================================

  return (
    <div>
      <Header1 />

      <Box p={4}>
        <Typography 
  variant="h4" 
  mb={3} 
  fontWeight="bold" 
  align="center"
>
  Request Details
</Typography>


        {/* Request Card */}
        <Box 
  display="flex" 
  justifyContent="center" 
  px={2}  // padding for small screens
  mt={3}
>
  <Card sx={{ 
      borderRadius: 3, 
      boxShadow: 3, 
      width: '100%',       // full width on small screens
      maxWidth: 600       // limit max width for large screens
    }}
  >
    {renderMedia(data.media_url)}
    <CardContent>
      {/* Header */}
      <Typography variant="h5" fontWeight="bold" mb={1} color="primary" align="center">
        {data.language}
      </Typography>

      {/* Description */}
      <Typography variant="body1" mb={2} align="center">
        {data.description}
      </Typography>

      {/* Info badges */}
      <Box display="flex" flexWrap="wrap" gap={1} justifyContent="center" mb={2}>
        <Box sx={{ bgcolor: 'primary.main', color: 'white', px: 2, py: 0.5, borderRadius: 1 }}>
          <Typography variant="body2"><strong>Date:</strong> {data.selectedDate}</Typography>
        </Box>
        <Box sx={{ bgcolor: 'secondary.main', color: 'white', px: 2, py: 0.5, borderRadius: 1 }}>
          <Typography variant="body2"><strong>Start:</strong> {data.startTime}</Typography>
        </Box>
        <Box sx={{ bgcolor: 'success.main', color: 'white', px: 2, py: 0.5, borderRadius: 1 }}>
          <Typography variant="body2"><strong>Duration:</strong> {data.duration} hrs</Typography>
        </Box>
        <Box sx={{ bgcolor: 'warning.main', color: 'white', px: 2, py: 0.5, borderRadius: 1 }}>
          <Typography variant="body2"><strong>Price:</strong> ${data.price}</Typography>
        </Box>
      </Box>

      {/* Optional divider */}
      <Divider sx={{ my: 1 }} />

      {/* Footer / Extra info if needed */}
      <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
        Request created on: {data.created_at} | Updated: {data.updated_at}
      </Typography>
    </CardContent>
  </Card>
</Box>
<Button
      variant="contained"
      color="primary"
      onClick={handleEdit}
    >
      Edit
    </Button>



        {/* Approved Experts */}
        <Box mt={4}>
          <Typography variant="h6" fontWeight="bold">Approved Experts</Typography>
          {approvedExperts.map(item => {
            const exp = item.expert;
            const user = item.user;

            return (
              <Card key={exp.id} sx={{ mt: 2, p: 2, cursor: "pointer" }} onClick={() => handleOpenModal({ ...exp, user_details: user })}>
                <Box display="flex" alignItems="center">
                  <Avatar src={user?.image || ""} sx={{ width: 60, height: 60, mr: 2 }} />
                  <Box>
                    <Typography fontWeight="bold">{user?.name} {user?.surname}</Typography>
                    <Typography variant="body2">{exp.designation}</Typography>
                    <Typography variant="body2">{user?.email}</Typography>
                  </Box>
                  <Button
                    color="error"
                    variant="contained"
                    onClick={(e) => { e.stopPropagation(); revokeExpert(exp.user_id); }}
                    sx={{ ml: "auto" }}
                  >
                    Revoke
                  </Button>
                </Box>
              </Card>
            );
          })}
        </Box>

        <Box mt={4}>
  <Typography variant="h6" fontWeight="bold">Find & Approve Experts</Typography>
  <ExpertSearch
    requestId={id}
    onApprove={(expertId) => approveExpert(expertId)}
  />
</Box>



        {/* Predict Experts */}
        <Box mt={4}>
          <Button 
            variant="contained" 
            onClick={predictExperts}
            disabled={loadingExperts}
          >
            {loadingExperts ? "Predicting..." : "Predict Experts"}
          </Button>

          {experts.map(e => {
            const user = e.user_details;

            return (
              <Card key={e.expert_id} sx={{ mt: 2, p: 2, cursor: "pointer" }} onClick={() => handleOpenModal(e)}>
                <Box display="flex" alignItems="center">

                  <Avatar 
                    src={user?.image ? user.image : ""}

                    sx={{ width: 60, height: 60, mr: 2 }}
                  />

                  <Box>
                    <Typography fontWeight="bold">{user?.name} {user?.surname}</Typography>
                    <Typography variant="body2">{e.designation}</Typography>
                    <Typography variant="body2">{user?.email}</Typography>
                    <Typography variant="body2">Contact: {user?.contact}</Typography>
                  </Box>

                  <Button 
                    variant="contained"
                    sx={{ ml: "auto" }}
                    onClick={(ev) => { ev.stopPropagation(); approveExpert(e.expert_user_id); }}
                  >
                    Approve
                  </Button>

                </Box>
              </Card>
            );
          })}

        </Box>



        {/* ================================================ */}
        {/* ============== EXPERT DETAILS MODAL ============ */}
        {/* ================================================ */}

        <Modal open={openModal} onClose={handleCloseModal}>
          <Box sx={modalStyle}>
            
            {selectedExpert && (
              <>

                <Box textAlign="center">
                  <Avatar 
                    src={selectedExpert.user_details?.image ? selectedExpert.user_details.image : ""}
                    sx={{ width: 90, height: 90, margin: "auto" }}
                  />
                 
                  <Typography variant="h6" mt={2}>
                    {selectedExpert.user_details?.name} {selectedExpert.user_details?.surname}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedExpert.designation}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography><strong>Email:</strong> {selectedExpert.user_details?.email}</Typography>
                <Typography><strong>Contact:</strong> {selectedExpert.user_details?.contact}</Typography>
                <Typography><strong>Gender:</strong> {selectedExpert.user_details?.gender}</Typography>
                <Typography><strong>DOB:</strong> {selectedExpert.user_details?.dob}</Typography>

                <Divider sx={{ my: 2 }} />

                <Typography><strong>Expertise:</strong></Typography>
                <Typography variant="body2">{selectedExpert.areas_of_expertise}</Typography>

                <Typography mt={2}><strong>About:</strong></Typography>
                <Typography variant="body2">{selectedExpert.about}</Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="caption">
                  Created: {selectedExpert.created_at}
                </Typography><br />
                <Typography variant="caption">
                  Updated: {selectedExpert.updated_at}
                </Typography>

                <Box textAlign="center" mt={3}>
                  <Button variant="contained" onClick={handleCloseModal}>Close</Button>
                </Box>

              </>
            )}
          </Box>
        </Modal>

      </Box>

      <Footer2 />
    </div>
  );
};

export default RequestDetailsPage;
